// src/theme/uikitPalette.ts
import { colors } from './tokens.colors';
import { ThemeColor } from './types';

export interface UIKitColorRole {
  /**
   * Color de fondo principal para ese color de marca.
   * Ej: botones filled, toggles ON, chips destacados, etc.
   */
  main: string;

  /**
   * Color de fondo claro para contenedores / estados menos enfáticos.
   * Ej: botones tonales, chips suaves, fondos de cards relacionadas.
   */
  light: string;

  /**
   * Color de texto / iconos cuando el fondo es "light".
   * Ej: texto sobre Primary Light, Secondary Light, etc.
   */
  dark: string;

  /**
   * Color de texto / iconos cuando el fondo es "main".
   * Es el equivalente a "On Primary", "On Secondary", etc. en Figma.
   */
  on: string;
}

/**
 * Paleta UIKit basada en los tokens de Figma.
 *
 * Convención:
 *  - main  → token 500 (color de marca)
 *  - light → token 100/200 (según figma “Light”)
 *  - dark  → token 700/800 (según figma “Dark”)
 *  - on    → color de texto/icono sobre main
 *
 * Uso recomendado:
 *  - Fondo = main   → texto/iconos = on
 *  - Fondo = light  → texto/iconos = dark
 */
export const uikitPalette: Record<ThemeColor, UIKitColorRole> = {
  primary: {
    main: colors.primary[400],
    light: colors.primary[100],
    dark: colors.primary[700],
    on: colors.specialColors.white, // On Primary (texto/iconos sobre primary)
  },
  secondary: {
    main: colors.secondary[500],
    light: colors.secondary[200],
    dark: colors.secondary[700],
    on: colors.specialColors.white, // On Secondary
  },
  tertiary: {
    main: colors.tertiary[600],
    light: colors.tertiary[200],
    dark: colors.tertiary[800],
    on: colors.specialColors.white, // On Tertiary
  },
  success: {
    main: colors.success[500],
    light: colors.success[100],
    dark: colors.success[800],
    on: colors.specialColors.white, // On Success
  },
  error: {
    main: colors.error[500],
    light: colors.error[100],
    dark: colors.error[700],
    on: colors.specialColors.white, // On Error
  },
  warning: {
    main: colors.warning[500],
    light: colors.warning[100],
    dark: colors.warning[700],
    on: colors.specialColors.white, // On Warning
  },
};

/**
 * Roles de texto globales sacados de la hoja “Borders and Text”.
 *
 * - primary: texto principal
 * - secondary: texto secundario / menos énfasis
 * - disabled: texto deshabilitado
 * - contrast: texto sobre fondos muy oscuros o intensos
 * - placeholder: placeholder en inputs
 * - link: enlaces
 */
export const textColors = {
  primary: colors.neutralVariant[700], // Text-primary
  secondary: colors.neutralVariant[400], // Text-secondary
  disabled: colors.neutralVariant[300], // Text-disabled
  contrast: colors.specialColors.white, // Text-contrastText
  placeholder: colors.neutral[300], // Text-placeholder
  link: colors.primary[500], // Text-link
};

/**
 * Roles de borde globales sacados de “Borders and text”.
 *
 * Neutral 100 = outline (según la hoja).
 */
export const borderColors = {
  neutral200: colors.neutral[200], // Border-neutral200
  neutral100: colors.neutral[100], // Border-neutral100 (Outline)
  neutral50: colors.neutral[50], // Border-neutral50
};

export const surfaceColors = {
  appBackground: colors.specialColors.bgVariant1, // fondo general de la app
  card: colors.specialColors.white, // cards/dialogs
};

export const getUIKitColor = (color: ThemeColor = 'primary') => uikitPalette[color];
